<script>
import { getInboxClassByType } from 'dashboard/helper/inbox';
import {
  getPhoneCallData,
  isMissedCall,
  isUnansweredMissedCall,
} from 'dashboard/helper/phoneHelper';

export default {
  props: {
    inbox: {
      type: Object,
      default: () => {},
    },
    conversation: {
      type: Object,
      default: () => {},
    },
    displayMode: {
      type: String,
      default: 'full',
    },
  },
  computed: {
    computedInboxClass() {
      const { phone_number: phoneNumber, channel_type: type } = this.inbox;
      return getInboxClassByType(type, phoneNumber);
    },
    isPhoneChannel() {
      return this.inbox?.channel_type === 'Channel::Phone';
    },
    phoneCallData() {
      return getPhoneCallData(this.conversation);
    },
    phoneIconClass() {
      if (!this.isPhoneChannel) return '';

      const { direction, connected, agent_handled } = this.phoneCallData;

      if (direction === 'outbound') {
        if (connected === 'true' || connected === true) {
          return 'phone-icon--outbound-connected';
        }
        return 'phone-icon--outbound-failed';
      }
      if (direction === 'inbound') {
        if (isUnansweredMissedCall(this.conversation)) {
          return 'phone-icon--inbound-unanswered-missed';
        }
        if (isMissedCall(this.conversation)) {
          return 'phone-icon--inbound-missed';
        }
        if (agent_handled === 'true' || agent_handled === true) {
          return 'phone-icon--inbound-agent';
        }
        return 'phone-icon--inbound-ivr';
      }

      return 'phone-icon--default';
    },
    phoneIcon() {
      if (!this.isPhoneChannel) return this.computedInboxClass;

      const { direction } = this.phoneCallData;

      if (direction === 'outbound') {
        return 'call-outbound';
      }

      if (direction === 'inbound') {
        return 'call-inbound';
      }

      return 'call';
    },
    phoneIconType() {
      if (!this.isPhoneChannel) return 'outline';
      return 'filled';
    },
    phoneTooltip() {
      if (!this.isPhoneChannel) return '';

      const { direction, connected, agent_handled } = this.phoneCallData;

      if (direction === 'outbound') {
        if (connected === 'true' || connected === true) {
          return this.$t('INBOX.PHONE_CALL_TYPES.OUTBOUND_CONNECTED');
        }
        return this.$t('INBOX.PHONE_CALL_TYPES.OUTBOUND_FAILED');
      }
      if (direction === 'inbound') {
        if (isUnansweredMissedCall(this.conversation)) {
          return this.$t('INBOX.PHONE_CALL_TYPES.INBOUND_UNANSWERED_MISSED');
        }
        if (isMissedCall(this.conversation)) {
          return this.$t('INBOX.PHONE_CALL_TYPES.INBOUND_MISSED');
        }
        if (agent_handled === 'true' || agent_handled === true) {
          return this.$t('INBOX.PHONE_CALL_TYPES.INBOUND_AGENT');
        }
        return this.$t('INBOX.PHONE_CALL_TYPES.INBOUND_IVR');
      }

      return this.$t('INBOX.PHONE_CALL_TYPES.DEFAULT');
    },
    missedCallCount() {
      return this.conversation?.additional_attributes?.missed_call_count || 0;
    },
    showMissedCallCount() {
      const { direction } = this.phoneCallData;

      if (
        !this.isPhoneChannel ||
        this.missedCallCount === 0 ||
        direction === 'outbound'
      )
        return false;

      const { missed } = this.phoneCallData;

      return missed === true;
    },
  },
};
</script>

<template>
  <div
    v-tooltip.top="isPhoneChannel ? phoneTooltip : ''"
    class_old="inbox--name inline-flex items-center py-0.5 px-0 leading-3 whitespace-nowrap bg-none text-slate-600 dark:text-slate-500 text-xs my-0 mx-1"
    class="inbox--name inline-flex items-center min-w-0 max-w-full py-0.5 px-0 leading-3 whitespace-nowrap bg-none text-n-slate-11 text-xs my-0 mx-2.5"
  >
    <fluent-icon
      class="flex-shrink-0 mr-0.5 rtl:ml-0.5 rtl:mr-0 phone-icon"
      :class="[phoneIconClass]"
      :icon="phoneIcon"
      :type="phoneIconType"
      size="14"
    />
    <span v-if="showMissedCallCount" class="flex-shrink-0 missed-call-count">
      {{ missedCallCount }}
    </span>
    <span
      v-if="displayMode === 'full'"
      class="overflow-hidden text-ellipsis whitespace-nowrap"
    >
      {{ inbox.internal_name || inbox.name }}
    </span>
  </div>
</template>

<style lang="scss" scoped>
.phone-icon {
  transition: color 0.2s ease-in-out;

  &.phone-icon--outbound-connected,
  &.phone-icon--inbound-agent {
    color: #22c55e; // green-500
  }

  &.phone-icon--outbound-failed,
  &.phone-icon--inbound-unanswered-missed {
    color: #ef4444; // red-500
  }

  &.phone-icon--inbound-missed {
    color: #f97316; // orange-500
  }

  &.phone-icon--inbound-ivr {
    color: #3b82f6; // blue-500
  }

  &.phone-icon--default {
    color: inherit;
  }
}

.missed-call-count {
  font-size: 11px;
  font-weight: 600;
  color: #f97316; // orange-500
  margin-right: 5px;
}
</style>
